import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SpaceDefinition, SpaceId } from '@respaldos/shared';
import { AccessDeniedError, SPACES } from '@respaldos/shared';
import { useAuth, usePortalLogout } from './hooks/useAuth';
import {
  Sidebar,
  TopBar,
  type NavSelection,
  type TopSection,
} from './components/Shell';
import {
  ActivityView,
  ArchitecturePanel,
  FilesView,
  PreviewModal,
  PromptDialog,
  StatusBar,
} from './components/Portal';
import { AdminUsersPage } from './components/AdminUsersPage';
import {
  createFolder,
  deleteObject,
  fetchActivity,
  fetchFiles,
  fetchSpaces,
  getSpaceMeta,
  moveObjectPath,
  requestDownloadUrl,
  uploadFile,
  type SessionUser,
} from './lib/api';
import type { ActivityItem, FileItem, UploadProgress } from './lib/types';

function spacePrefix(spaceId: SpaceId): string {
  return SPACES[spaceId].prefix;
}

function relFromFolderKey(spaceId: SpaceId, folderKey: string): string {
  const prefix = spacePrefix(spaceId);
  const rel = folderKey.startsWith(prefix)
    ? folderKey.slice(prefix.length)
    : folderKey;
  return rel.replace(/\/$/, '');
}

function PortalApp({ user }: { user: SessionUser }) {
  const logout = usePortalLogout();
  const isAdmin = user.groups.includes('Admins');
  const [spaces, setSpaces] = useState<SpaceDefinition[]>([]);
  const [selected, setSelected] = useState<NavSelection>('marketing');
  const [tab, setTab] = useState<'files' | 'activity'>('files');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [currentPath, setCurrentPath] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [lastSpace, setLastSpace] = useState<SpaceId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null,
  );
  const [mkdirOpen, setMkdirOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchSpaces(user);
        if (cancelled) return;
        setSpaces(s);
        const first = s[0]?.id ?? 'activity';
        setSelected(first);
        if (first !== 'activity') setLastSpace(first);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Error');
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadFiles = useCallback(
    async (spaceId: SpaceId, pathRel = '') => {
      setListLoading(true);
      setError(null);
      try {
        const items = await fetchFiles(user, spaceId, pathRel);
        setFiles(items);
        setSelectedKeys((prev) => {
          const next = new Set<string>();
          for (const k of prev) {
            if (items.some((i) => i.key === k)) next.add(k);
          }
          return next;
        });
      } catch (e) {
        if (e instanceof AccessDeniedError) {
          setError(e.message);
          setFiles([]);
        } else {
          setError(e instanceof Error ? e.message : 'Error al listar');
        }
      } finally {
        setListLoading(false);
      }
    },
    [user],
  );

  const loadActivity = useCallback(async () => {
    try {
      setActivity(await fetchActivity(user));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de auditoría');
    }
  }, [user]);

  useEffect(() => {
    if (selected === 'activity' || selected === 'users') {
      if (selected === 'activity') {
        setTab('activity');
        void loadActivity();
      }
      return;
    }
  }, [selected, loadActivity]);

  useEffect(() => {
    if (selected === 'activity' || selected === 'users') return;
    if (tab === 'activity') void loadActivity();
  }, [tab, selected, loadActivity]);

  useEffect(() => {
    if (selected === 'activity' || selected === 'users') return;
    void loadFiles(selected, currentPath);
  }, [currentPath, selected, loadFiles]);

  const statusText = useMemo(() => {
    if (selected === 'activity' || selected === 'users') return '';
    return getSpaceMeta(selected, currentPath).status;
  }, [selected, currentPath]);

  const selectedItems = useMemo(
    () => files.filter((f) => selectedKeys.has(f.key)),
    [files, selectedKeys],
  );

  async function handleUpload(fileList: FileList | null) {
    if (!fileList || selected === 'activity' || selected === 'users') return;
    if (busy) return;
    const list = Array.from(fileList);
    if (list.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        setUploadProgress({
          fileName: file.name,
          percent: 0,
          index: i + 1,
          total: list.length,
        });
        await uploadFile(user, selected, currentPath, file, (percent) => {
          setUploadProgress({
            fileName: file.name,
            percent,
            index: i + 1,
            total: list.length,
          });
        });
      }
      setUploadOpen(false);
      await loadFiles(selected, currentPath);
      await loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al subir');
    } finally {
      setUploadProgress(null);
      setBusy(false);
    }
  }

  async function handleMkdir(name: string) {
    if (selected === 'activity' || selected === 'users') return;
    if (busy) return;
    setMkdirOpen(false);
    setBusy(true);
    setError(null);
    try {
      await createFolder(user, selected, currentPath, name);
      await loadFiles(selected, currentPath);
      await loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear carpeta');
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload() {
    if (selected === 'activity' || selected === 'users') return;
    if (selectedItems.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const filesOnly = selectedItems.filter((f) => !f.isFolder);
      const folders = selectedItems.filter((f) => f.isFolder);
      if (folders.length > 0 && filesOnly.length === 0) {
        setError(
          'Descargar carpetas enteras como ZIP no está disponible. Abra la carpeta y seleccione archivos.',
        );
        return;
      }
      for (const f of filesOnly) {
        const { url } = await requestDownloadUrl(user, selected, f.key);
        const a = document.createElement('a');
        a.href = url;
        a.download = f.name;
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      if (folders.length > 0) {
        setError(
          `Descargados ${filesOnly.length} archivo(s). Carpetas omitidas (sin ZIP).`,
        );
      }
      await loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al descargar');
    } finally {
      setBusy(false);
    }
  }

  async function handleMove(destRel: string) {
    if (selected === 'activity' || selected === 'users') return;
    if (busy) return;
    setMoveOpen(false);
    setBusy(true);
    setError(null);
    try {
      const destClean = destRel
        .replace(/^\/+/, '')
        .replace(/\/$/, '')
        .replace(/^marketing\//i, '')
        .replace(/^db\//i, '');
      if (destClean.includes('..')) {
        throw new Error('Ruta de destino inválida');
      }
      const prefix = spacePrefix(selected);
      for (const item of selectedItems) {
        const baseName = item.name;
        const toKey = item.isFolder
          ? `${prefix}${destClean ? `${destClean}/` : ''}${baseName}/`.replace(
              /\/{2,}/g,
              '/',
            )
          : `${prefix}${destClean ? `${destClean}/` : ''}${baseName}`.replace(
              /\/{2,}/g,
              '/',
            );
        await moveObjectPath(user, selected, item.key, toKey);
      }
      await loadFiles(selected, currentPath);
      await loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al mover');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (selected !== 'marketing' || busy) return;
    if (selectedItems.length === 0) return;
    const ok = window.confirm(
      `¿Mover ${selectedItems.length} elemento(s) a la papelera?`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      for (const item of selectedItems) {
        await deleteObject(user, 'marketing', item.key);
      }
      await loadFiles('marketing', currentPath);
      await loadActivity();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    } finally {
      setBusy(false);
    }
  }

  async function openPreview(file: FileItem) {
    if (selected === 'activity' || selected === 'users') return;
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const { url } = await requestDownloadUrl(user, selected, file.key);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewError(
        e instanceof Error ? e.message : 'No se pudo abrir la vista previa',
      );
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    setPreviewFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }

  function openFolder(folder: FileItem) {
    if (selected === 'activity' || selected === 'users') return;
    setCurrentPath(relFromFolderKey(selected, folder.key));
  }

  const isActivityNav = selected === 'activity';
  const isUsersNav = selected === 'users';
  const showFilesTabs = !isActivityNav && !isUsersNav;
  const showActivity = tab === 'activity' || isActivityNav;
  const activeSpaceId: SpaceId | null =
    isActivityNav || isUsersNav ? null : selected;
  const section: TopSection = isUsersNav
    ? 'users'
    : isActivityNav
      ? 'activity'
      : 'files';

  function selectNav(id: NavSelection) {
    setSelected(id);
    setCurrentPath('');
    if (id !== 'activity' && id !== 'users') setLastSpace(id);
  }

  function selectSection(next: TopSection) {
    if (next === 'files') {
      setTab('files');
      const target = lastSpace ?? spaces[0]?.id;
      if (target) {
        setSelected(target);
        setCurrentPath('');
      }
      return;
    }
    setSelected(next);
    setCurrentPath('');
  }

  return (
    <div className="app-frame">
      {bootLoading && <p className="loading-inline">Cargando espacios…</p>}
      <TopBar
        user={user}
        section={section}
        onSection={selectSection}
        isAdmin={isAdmin}
        onLogout={() => logout()}
      />
      <div className="shell">
        <Sidebar
          spaces={spaces}
          selected={selected}
          onSelect={selectNav}
          isAdmin={isAdmin}
        />
        <main className="main">
          {!bootLoading && showFilesTabs && (
            <div className="view-tabs">
              <button
                type="button"
                className={`tab-btn ${tab === 'files' ? 'active' : ''}`}
                onClick={() => setTab('files')}
              >
                Archivos
              </button>
              <button
                type="button"
                className={`tab-btn ${tab === 'activity' ? 'active' : ''}`}
                onClick={() => setTab('activity')}
              >
                Actividad
              </button>
            </div>
          )}

          {error && <div className="error-banner">{error}</div>}

          {!bootLoading && isUsersNav && <AdminUsersPage user={user} />}

          {!bootLoading && tab === 'files' && activeSpaceId && (
            <FilesView
              spaceId={activeSpaceId}
              files={files}
              selectedKeys={selectedKeys}
              currentPath={currentPath}
              onNavigate={setCurrentPath}
              onOpenFolder={openFolder}
              onPreviewFile={(f) => void openPreview(f)}
              onToggleSelect={(key) => {
                setSelectedKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
              onSelectAll={(checked) => {
                setSelectedKeys(
                  checked ? new Set(files.map((f) => f.key)) : new Set(),
                );
              }}
              onUpload={handleUpload}
              uploadOpen={uploadOpen}
              onToggleUpload={setUploadOpen}
              onNewFolder={() => setMkdirOpen(true)}
              onDownload={() => void handleDownload()}
              onMove={() => setMoveOpen(true)}
              onDelete={() => void handleDelete()}
              search={search}
              onSearch={setSearch}
              busy={busy}
              listLoading={listLoading}
              uploadProgress={uploadProgress}
            />
          )}

          {!bootLoading && showActivity && !isUsersNav && (
            <ActivityView events={activity} />
          )}

          {!bootLoading && !isUsersNav && isAdmin && <ArchitecturePanel />}
        </main>
      </div>
      <StatusBar
        text={
          isUsersNav ? 'Administración · usuarios Cognito' : statusText
        }
        isActivity={isActivityNav || (!isUsersNav && tab === 'activity')}
        eventCount={activity.length}
        showAccount={isAdmin}
      />

      {mkdirOpen && (
        <PromptDialog
          title="Nueva carpeta"
          label="Nombre de la carpeta"
          placeholder="Ej. Photography"
          confirmLabel="Crear"
          busy={busy}
          onCancel={() => setMkdirOpen(false)}
          onConfirm={(name) => void handleMkdir(name)}
        />
      )}

      {moveOpen && (
        <PromptDialog
          title="Mover"
          label="Carpeta destino (ruta dentro de este espacio)"
          placeholder={
            currentPath
              ? `Ej. ${currentPath}/Archivado`
              : 'Ej. 2026/Archivado'
          }
          confirmLabel="Mover"
          busy={busy}
          onCancel={() => setMoveOpen(false)}
          onConfirm={(dest) => void handleMove(dest)}
        />
      )}

      {previewFile && (
        <PreviewModal
          file={previewFile}
          url={previewUrl}
          loading={previewLoading}
          error={previewError}
          onClose={closePreview}
          onDownload={() => {
            if (!previewUrl) return;
            const a = document.createElement('a');
            a.href = previewUrl;
            a.download = previewFile.name;
            a.rel = 'noopener';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            a.remove();
          }}
        />
      )}
    </div>
  );
}

export default function App() {
  const { user } = useAuth();
  if (!user) return null;
  return <PortalApp user={user} />;
}
